import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { StationAlias } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputText,
    ValidatedText,
    InputSelect,
    DataSourceLookup,
    InputNullableDateTime,
    DateComp,
    InputBool,
    DisplayBool,
} from '@/components';
import { useEventCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import { type DateType } from '@/utilities/dateutils';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';
import { useValidator } from '@/composeables';

export class StationAliasGuiDef extends GuiDef<StationAlias> {
    constructor() {
        super('StationAlias');

        const EventCommon = storeToRefs(useEventCommonStore());
        const StationsSource = computed(() => {
            return EventCommon.StationsAsSelectLine.value;
        });

        const AliasValidator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        const Alias = new FieldDef<StationAlias>();
        Alias.Name = 'Alias';
        Alias.FieldName = 'Alias';
        Alias.DefinedFor = 'Both';
        Alias.TranslationKey = 'stationalias.alias';
        Alias.Editable = true;
        Alias.Filterable = true;
        Alias.Sortable = true;
        Alias.CssClasses = '';
        Alias.value = (stationalias) => stationalias.Alias;
        Alias.setValue = (stationalias, value) => (stationalias.Alias = value);
        Alias.DetailEditor = markRaw(InputText);
        Alias.DetailEditorProps = { modelModifiers: { uppercase: true } };
        Alias.CellEditor = markRaw(InputText);
        Alias.CellEditorProps = { modelModifiers: { uppercase: true } };
        Alias.CellRenderer = markRaw(ValidatedText);
        Alias.CellRendererProps = { modelModifiers: { uppercase: true } };
        Alias.Validator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        Alias.SortDirection = undefined;
        Alias.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(AliasValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(AliasValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        Alias.MinWidth = undefined;
        Alias.MaxWidth = undefined;
        Alias.Tooltip = null;
        this.Fields.push(Alias);

        const StationID = new FieldDef<StationAlias>();
        StationID.Name = 'StationID';
        StationID.FieldName = 'StationID';
        StationID.DefinedFor = 'Both';
        StationID.TranslationKey = 'stationalias.stationid';
        StationID.Editable = true;
        StationID.Filterable = true;
        StationID.Sortable = true;
        StationID.CssClasses = '';
        StationID.value = (stationalias) => stationalias.StationID;
        StationID.setValue = (stationalias, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            stationalias.StationID = value;
        };
        StationID.DetailEditor = markRaw(InputSelect);
        StationID.DetailEditorProps = { datasource: { mapsource: StationsSource } };
        StationID.CellEditor = markRaw(InputSelect);
        StationID.CellEditorProps = { datasource: { mapsource: StationsSource } };
        // @ts-expect-error @ts-ignore
        StationID.CellRenderer = markRaw(DataSourceLookup);
        StationID.CellRendererProps = { datasource: { mapsource: StationsSource } };
        StationID.ExportValueFormatter = (obj: StationAlias) => {
            return StationsSource.value.get(obj.StationID)?.text;
        };
        StationID.Validator = null;
        StationID.SortDirection = undefined;
        StationID.cellClassRules = undefined;
        StationID.MinWidth = undefined;
        StationID.MaxWidth = undefined;
        StationID.Tooltip = null;
        this.Fields.push(StationID);

        const ActiveFromValidator = new ValidatorDef({ Min: null, Max: null, Required: null, Type: 'eventactivetime' });
        const ActiveFrom = new FieldDef<StationAlias>();
        ActiveFrom.Name = 'ActiveFrom';
        ActiveFrom.FieldName = 'ActiveFrom';
        ActiveFrom.DefinedFor = 'Both';
        ActiveFrom.TranslationKey = 'stationalias.activefrom';
        ActiveFrom.Editable = true;
        ActiveFrom.Filterable = true;
        ActiveFrom.Sortable = true;
        ActiveFrom.CssClasses = '';
        ActiveFrom.value = (stationalias) => stationalias.ActiveFrom;
        ActiveFrom.setValue = (stationalias, value) => (stationalias.ActiveFrom = value);
        ActiveFrom.DetailEditor = markRaw(InputNullableDateTime);
        ActiveFrom.DetailEditorProps = { format: 'DATETIME' as DateType };
        ActiveFrom.CellEditor = markRaw(InputNullableDateTime);
        ActiveFrom.CellEditorProps = { format: 'DATETIME' as DateType };
        ActiveFrom.CellRenderer = markRaw(DateComp);
        ActiveFrom.CellRendererProps = { format: 'DATETIME' as DateType };
        ActiveFrom.Validator = new ValidatorDef({ Min: null, Max: null, Required: null, Type: 'eventactivetime' });
        ActiveFrom.SortDirection = undefined;
        ActiveFrom.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(ActiveFromValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(ActiveFromValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        ActiveFrom.MinWidth = 180;
        ActiveFrom.MaxWidth = undefined;
        ActiveFrom.Tooltip = null;
        this.Fields.push(ActiveFrom);

        const ActiveToValidator = new ValidatorDef({ Min: null, Max: null, Required: null, Type: 'eventactivetime' });
        const ActiveTo = new FieldDef<StationAlias>();
        ActiveTo.Name = 'ActiveTo';
        ActiveTo.FieldName = 'ActiveTo';
        ActiveTo.DefinedFor = 'Both';
        ActiveTo.TranslationKey = 'stationalias.activeto';
        ActiveTo.Editable = true;
        ActiveTo.Filterable = true;
        ActiveTo.Sortable = true;
        ActiveTo.CssClasses = '';
        ActiveTo.value = (stationalias) => stationalias.ActiveTo;
        ActiveTo.setValue = (stationalias, value) => (stationalias.ActiveTo = value);
        ActiveTo.DetailEditor = markRaw(InputNullableDateTime);
        ActiveTo.DetailEditorProps = { format: 'DATETIME' as DateType, prefillUsingEventDate: true };
        ActiveTo.CellEditor = markRaw(InputNullableDateTime);
        ActiveTo.CellEditorProps = { format: 'DATETIME' as DateType, prefillUsingEventDate: true };
        ActiveTo.CellRenderer = markRaw(DateComp);
        ActiveTo.CellRendererProps = { format: 'DATETIME' as DateType, prefillUsingEventDate: true };
        ActiveTo.Validator = new ValidatorDef({ Min: null, Max: null, Required: null, Type: 'eventactivetime' });
        ActiveTo.SortDirection = undefined;
        ActiveTo.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(ActiveToValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(ActiveToValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        ActiveTo.MinWidth = 180;
        ActiveTo.MaxWidth = undefined;
        ActiveTo.Tooltip = null;
        this.Fields.push(ActiveTo);

        const ReadsBib = new FieldDef<StationAlias>();
        ReadsBib.Name = 'ReadsBib';
        ReadsBib.FieldName = 'ReadsBib';
        ReadsBib.DefinedFor = 'Both';
        ReadsBib.TranslationKey = 'stationalias.readsbib';
        ReadsBib.Editable = true;
        ReadsBib.Filterable = false;
        ReadsBib.Sortable = true;
        ReadsBib.CssClasses = '';
        ReadsBib.value = (stationalias) => stationalias.ReadsBib;
        ReadsBib.setValue = (stationalias, value) => (stationalias.ReadsBib = value);
        ReadsBib.DetailEditor = markRaw(InputBool);
        ReadsBib.DetailEditorProps = { label: 'stationalias.readsbib' };
        ReadsBib.CellEditor = markRaw(InputBool);
        ReadsBib.CellEditorProps = {};
        ReadsBib.CellRenderer = markRaw(DisplayBool);
        ReadsBib.CellRendererProps = {};
        ReadsBib.Validator = null;
        ReadsBib.SortDirection = undefined;
        ReadsBib.cellClassRules = undefined;
        ReadsBib.MinWidth = undefined;
        ReadsBib.MaxWidth = undefined;
        ReadsBib.Tooltip = 'stationalias.readsbibtooltip';
        this.Fields.push(ReadsBib);

        const ReadsChip = new FieldDef<StationAlias>();
        ReadsChip.Name = 'ReadsChip';
        ReadsChip.FieldName = 'ReadsChip';
        ReadsChip.DefinedFor = 'Both';
        ReadsChip.TranslationKey = 'stationalias.readschip';
        ReadsChip.Editable = true;
        ReadsChip.Filterable = false;
        ReadsChip.Sortable = true;
        ReadsChip.CssClasses = '';
        ReadsChip.value = (stationalias) => stationalias.ReadsChip;
        ReadsChip.setValue = (stationalias, value) => (stationalias.ReadsChip = value);
        ReadsChip.DetailEditor = markRaw(InputBool);
        ReadsChip.DetailEditorProps = { label: 'stationalias.readschip' };
        ReadsChip.CellEditor = markRaw(InputBool);
        ReadsChip.CellEditorProps = {};
        ReadsChip.CellRenderer = markRaw(DisplayBool);
        ReadsChip.CellRendererProps = {};
        ReadsChip.Validator = null;
        ReadsChip.SortDirection = undefined;
        ReadsChip.cellClassRules = undefined;
        ReadsChip.MinWidth = undefined;
        ReadsChip.MaxWidth = undefined;
        ReadsChip.Tooltip = 'stationalias.readschiptooltip';
        this.Fields.push(ReadsChip);

        const BibOffset = new FieldDef<StationAlias>();
        BibOffset.Name = 'BibOffset';
        BibOffset.FieldName = 'BibOffset';
        BibOffset.DefinedFor = 'Both';
        BibOffset.TranslationKey = 'stationalias.biboffset';
        BibOffset.Editable = true;
        BibOffset.Filterable = false;
        BibOffset.Sortable = true;
        BibOffset.CssClasses = '';
        BibOffset.value = (stationalias) => stationalias.BibOffset;
        BibOffset.setValue = (stationalias, value) => (stationalias.BibOffset = value);
        BibOffset.DetailEditor = markRaw(InputBool);
        BibOffset.DetailEditorProps = { label: 'stationalias.biboffset' };
        BibOffset.CellEditor = markRaw(InputBool);
        BibOffset.CellEditorProps = {};
        BibOffset.CellRenderer = markRaw(DisplayBool);
        BibOffset.CellRendererProps = {};
        BibOffset.Validator = null;
        BibOffset.SortDirection = undefined;
        BibOffset.cellClassRules = undefined;
        BibOffset.MinWidth = undefined;
        BibOffset.MaxWidth = undefined;
        BibOffset.Tooltip = 'stationalias.biboffset';
        this.Fields.push(BibOffset);
    }

    public async LoadDependencies() {
        await useEventCommonStore().EnsureLoaded(['Stations']);
    }
}
